import React, { useState, useEffect } from "react";
import api from "../services/api";

const Departamentos = () => {
  const [departamentos, setDepartamentos] = useState([]);
  const [form, setForm] = useState({ nombre: "", codigo: "", descripcion: "", activo: true });
  const [editando, setEditando] = useState(false);
  const [departamentoId, setDepartamentoId] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [mensaje, setMensaje] = useState({ texto: "", tipo: "" });

  useEffect(() => {
    obtenerDepartamentos();
  }, []);

  const obtenerDepartamentos = async () => {
    try {
      setCargando(true);
      const res = await api.get("/departamentos");
      console.log("📥 Departamentos recibidos:", res.data); // 👈 Para depurar
      setDepartamentos(res.data);
      setMensaje({ texto: "", tipo: "" });
    } catch (error) {
      console.error("Error al cargar departamentos:", error);
      setMensaje({ texto: "❌ Error al cargar departamentos", tipo: "error" });
    } finally {
      setCargando(false);
    }
  };

  const guardarDepartamento = async () => {
    if (!form.nombre.trim()) {
      setMensaje({ texto: "⚠️ El nombre es obligatorio", tipo: "error" });
      return;
    }

    try {
      if (editando) {
        await api.put(`/departamentos/${departamentoId}`, form);
        setMensaje({ texto: "✅ Departamento actualizado", tipo: "success" });
      } else {
        await api.post("/departamentos", form);
        setMensaje({ texto: "✅ Departamento creado", tipo: "success" });
      }
      
      setForm({ nombre: "", codigo: "", descripcion: "", activo: true });
      setEditando(false);
      setDepartamentoId(null);
      obtenerDepartamentos();
      
      setTimeout(() => setMensaje({ texto: "", tipo: "" }), 3000);
    } catch (error) {
      console.error("Error al guardar departamento:", error);
      setMensaje({ texto: "❌ Error al guardar departamento", tipo: "error" });
    }
  };

  const editarDepartamento = (depto) => {
    setForm({ 
      nombre: depto.nombre, 
      codigo: depto.codigo || "", 
      descripcion: depto.descripcion || "",
      activo: depto.activo !== false 
    });
    setDepartamentoId(depto.id);
    setEditando(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancelarEdicion = () => {
    setForm({ nombre: "", codigo: "", descripcion: "", activo: true });
    setEditando(false);
    setDepartamentoId(null);
  };

  const eliminarDepartamento = async (id) => {
    if (!window.confirm("¿Seguro que deseas eliminar este departamento?")) return;
    
    try {
      await api.delete(`/departamentos/${id}`);
      setMensaje({ texto: "✅ Departamento eliminado", tipo: "success" });
      obtenerDepartamentos();
      setTimeout(() => setMensaje({ texto: "", tipo: "" }), 3000);
    } catch (error) {
      console.error("Error al eliminar departamento:", error);
      setMensaje({ texto: "❌ Error al eliminar departamento", tipo: "error" });
    }
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerContent}>
          <h1 style={styles.title}>🏢 Gestión de Departamentos</h1>
          <p style={styles.subtitle}>
            {departamentos.length === 0 
              ? "Crea tu primer departamento" 
              : `Administra los ${departamentos.length} departamentos`}
          </p>
        </div>
      </div>

      {/* Mensaje global */}
      {mensaje.texto && (
        <div style={{
          ...styles.message,
          ...(mensaje.tipo === "success" ? styles.messageSuccess : styles.messageError)
        }}>
          {mensaje.texto}
        </div>
      )}

      {/* Formulario */}
      <div style={styles.card}>
        <h3 style={styles.cardTitle}>
          {editando ? "✏️ Editar Departamento" : "➕ Nuevo Departamento"}
        </h3>
        <div style={styles.formGrid}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Nombre *</label>
            <input
              type="text"
              placeholder="Ej: Recursos Humanos, Finanzas, IT"
              value={form.nombre}
              onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              style={styles.input}
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Código</label>
            <input
              type="text"
              placeholder="Ej: RH, FIN, IT"
              value={form.codigo}
              onChange={(e) => setForm({ ...form, codigo: e.target.value })}
              style={styles.input}
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Descripción</label>
            <input
              type="text"
              placeholder="Descripción del departamento"
              value={form.descripcion}
              onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
              style={styles.input}
            />
          </div>

          {editando && (
            <div style={styles.formGroup}>
              <label style={styles.label}>Estado</label>
              <select
                value={form.activo}
                onChange={(e) => setForm({ ...form, activo: e.target.value === 'true' })}
                style={styles.input}
              >
                <option value="true">Activo</option>
                <option value="false">Inactivo</option>
              </select>
            </div>
          )}
        </div>

        <div style={styles.formActions}>
          <button onClick={guardarDepartamento} style={styles.btnSuccess}>
            {editando ? "💾 Actualizar" : "✓ Guardar"}
          </button>
          {editando && (
            <button onClick={cancelarEdicion} style={styles.btnCancel}>
              Cancelar
            </button>
          )}
        </div>
      </div>

      {/* Lista de Departamentos */}
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <h3 style={styles.cardTitle}>📋 Lista de Departamentos</h3>
          <span style={styles.badge}>{departamentos.length} departamentos</span>
        </div>

        {cargando ? (
          <div style={styles.loadingContainer}>
            <p style={styles.loadingText}>Cargando departamentos...</p>
          </div>
        ) : departamentos.length === 0 ? (
          <div style={styles.emptyState}>
            <span style={styles.emptyIcon}>🏢</span>
            <p style={styles.emptyText}>No hay departamentos creados</p>
            <p style={styles.emptySubtext}>
              Usa el formulario arriba para crear tu primer departamento
            </p>
          </div>
        ) : (
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>ID</th>
                  <th style={styles.th}>Nombre</th>
                  <th style={styles.th}>Código</th>
                  <th style={styles.th}>Descripción</th>
                  <th style={styles.th}>Estado</th>
                  <th style={styles.th}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {departamentos.map((depto) => (
                  <tr 
                    key={depto.id}
                    style={styles.tr}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                  >
                    <td style={styles.td}>{depto.id}</td>
                    <td style={styles.tdNombre}>
                      <strong>{depto.nombre}</strong>
                    </td>
                    <td style={styles.td}>{depto.codigo || "-"}</td>
                    <td style={styles.td}>{depto.descripcion || "-"}</td>
                    <td style={styles.td}>
                      <span style={depto.activo ? styles.badgeActivo : styles.badgeInactivo}>
                        {depto.activo ? "✓ Activo" : "✕ Inactivo"}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <div style={styles.actions}>
                        <button
                          onClick={() => editarDepartamento(depto)}
                          style={styles.btnEdit}
                        >
                          ✏️ Editar
                        </button>
                        <button
                          onClick={() => eliminarDepartamento(depto.id)}
                          style={styles.btnDelete}
                        >
                          🗑️ Eliminar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// Estilos (se mantienen igual)
const styles = {
  container: {
    minHeight: '100vh',
    background: '#f8fafc',
    padding: '20px',
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
  },
  header: {
    marginBottom: '25px',
  },
  headerContent: {
    maxWidth: '900px',
    margin: '0 auto',
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1e293b',
    margin: '0 0 5px 0',
  },
  subtitle: {
    fontSize: '14px',
    color: '#64748b',
    margin: '0',
  },
  message: {
    padding: '12px 20px',
    borderRadius: '8px',
    marginBottom: '20px',
    fontSize: '14px',
    fontWeight: '500',
    textAlign: 'center',
    maxWidth: '900px',
    margin: '0 auto 20px auto',
  },
  messageSuccess: {
    background: '#d1fae5',
    color: '#065f46',
    border: '1px solid #10b981',
  },
  messageError: {
    background: '#fee2e2',
    color: '#991b1b',
    border: '1px solid #ef4444',
  },
  card: {
    background: 'white',
    borderRadius: '12px',
    padding: '25px',
    marginBottom: '20px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
    border: '1px solid #e2e8f0',
    maxWidth: '900px',
    margin: '0 auto 20px auto',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  },
  cardTitle: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#1e293b',
    margin: '0 0 20px 0',
  },
  badge: {
    background: '#667eea',
    color: 'white',
    padding: '6px 14px',
    borderRadius: '20px',
    fontSize: '13px',
    fontWeight: '600',
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '20px',
    marginBottom: '20px',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
  },
  label: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#475569',
    marginBottom: '6px',
  },
  input: {
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid #cbd5e1',
    borderRadius: '8px',
    outline: 'none',
    transition: 'all 0.2s ease',
  },
  formActions: {
    display: 'flex',
    gap: '12px',
  },
  btnSuccess: {
    padding: '12px 24px',
    background: '#10b981',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  btnCancel: {
    padding: '12px 24px',
    background: '#94a3b8',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  loadingContainer: {
    padding: '40px',
    textAlign: 'center',
  },
  loadingText: {
    color: '#64748b',
    fontSize: '14px',
  },
  emptyState: {
    padding: '60px 20px',
    textAlign: 'center',
  },
  emptyIcon: {
    fontSize: '64px',
    marginBottom: '15px',
    display: 'block',
    opacity: '0.5',
  },
  emptyText: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#475569',
    margin: '0 0 8px 0',
  },
  emptySubtext: {
    fontSize: '14px',
    color: '#94a3b8',
    margin: '0',
  },
  tableWrapper: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px',
  },
  th: {
    background: '#f8fafc',
    padding: '12px',
    textAlign: 'left',
    fontWeight: '700',
    color: '#475569',
    borderBottom: '2px solid #e2e8f0',
  },
  tr: {
    transition: 'background 0.2s ease',
  },
  td: {
    padding: '12px',
    borderBottom: '1px solid #f1f5f9',
    color: '#1e293b',
  },
  tdNombre: {
    padding: '12px',
    borderBottom: '1px solid #f1f5f9',
    color: '#667eea',
    fontWeight: '600',
  },
  actions: {
    display: 'flex',
    gap: '8px',
  },
  btnEdit: {
    padding: '6px 12px',
    background: '#fbbf24',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  btnDelete: {
    padding: '6px 12px',
    background: '#ef4444',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  badgeActivo: {
    padding: '4px 12px',
    background: '#d1fae5',
    color: '#065f46',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600',
  },
  badgeInactivo: {
    padding: '4px 12px',
    background: '#fee2e2',
    color: '#991b1b',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600',
  },
};

export default Departamentos;